import { IconChevronRight, IconHome } from '@tabler/icons-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { findMenuTrail } from './menu.js';
import classes from './PageHeader.module.css';
import { PageHelpButton, pageHelpKey, readPageHelp } from './PageHelp.js';
import { followLink, useShell } from './shell-context.js';

/** Panonun yolu: breadcrumb'ın ev ikonu buraya gider. */
const HOME_PATH = '/dashboard';

export interface PageHeaderProps {
  title: string;
  description?: string;
  /** Başlığın sağındaki kart (gemi kartı, tarih seçici); üste hizalı, dar ekranda alta iner. */
  aside?: ReactNode;
  /** Sayfa düzeyindeki eylemler (ör. "Add vessel"). */
  actions?: ReactNode;
  /** Breadcrumb'ın son öğesi menü adından farklıysa (alt sayfa). */
  crumb?: string;
  /** Başlığın solunda ≈ 28 px lacivert ikon; ekran okuyucudan gizlenir. */
  icon?: ReactNode;
  /**
   * Başlık (h1) görsel olarak gizli, ekran okuyucuda kalır: sayfanın adı içerikteki bir kartta
   * yazıyorsa (Stock In / Out; gemi-envanter-kumanya.jpeg).
   */
  titleHidden?: boolean;
  /**
   * Bilgi penceresinin anahtarı (ADR 0053): yoksa menüdeki izin son öğesi. Menüde olmayan sayfa
   * verir; içerik `help.<helpKey>`'de yoksa "i" düğmesi çizilmez.
   */
  helpKey?: string;
}

/**
 * Sayfa başlığı (BGM): üstte breadcrumb (ev ikonu ve menüdeki iz), büyük başlık, bir satır
 * açıklama; sağda gemi kartı ya da eylemler. Breadcrumb her sayfada çizilir, böylece başlığın yeri
 * her ekranda aynıdır (M6f G1-10). Bütün iç ekranlar aynı başlığı kullanır (ADR 0019).
 */
export function PageHeader({
  title,
  description,
  aside,
  actions,
  crumb,
  icon,
  titleHidden = false,
  helpKey,
}: PageHeaderProps) {
  const { t, i18n } = useTranslation();
  const shell = useShell();
  const trail = shell ? findMenuTrail(shell.path, shell.menu) : [];
  const crumbs = trail.map((item) => ({ path: item.path, label: t(`menu.${item.id}`) }));
  if (crumb) crumbs.push({ path: '', label: crumb });
  // Menüde olmayan sayfa: iz başlıktan (ev ikonu ve tek öğe)
  if (crumbs.length === 0) crumbs.push({ path: '', label: title });
  const home = shell?.homePath ?? HOME_PATH;
  const atHome = shell?.path === home;
  // "i" düğmesi (ADR 0053): başlığın yanında; başlık gizliyse breadcrumb satırında
  const key = helpKey ?? (shell ? pageHelpKey(shell.path, shell.menu) : null);
  const content = key ? readPageHelp(t, (k) => i18n.exists(k), key) : null;
  const help =
    key && content ? (
      <PageHelpButton helpKey={key} content={content} title={title} menu={shell?.menu} />
    ) : null;
  const breadcrumb = (
    <nav aria-label={t('shell.breadcrumb')}>
      <ol className={classes.crumbs}>
        <li className={classes.crumb}>
          {shell && !atHome ? (
            <a
              href={home}
              className={classes.home}
              aria-label={t('shell.home')}
              onClick={(event) => followLink(event, home, shell.navigate)}
            >
              <IconHome size={14} stroke={1.8} aria-hidden />
            </a>
          ) : (
            <span className={classes.home} aria-hidden>
              <IconHome size={14} stroke={1.8} />
            </span>
          )}
          <IconChevronRight size={12} className={classes.separator} aria-hidden />
        </li>
        {crumbs.map((item, index) => {
          const last = index === crumbs.length - 1;
          return (
            <li key={`${item.path}-${item.label}`} className={classes.crumb}>
              {last || !shell || !item.path ? (
                <span aria-current={last ? 'page' : undefined}>{item.label}</span>
              ) : (
                <a
                  href={item.path}
                  onClick={(event) => followLink(event, item.path, shell.navigate)}
                >
                  {item.label}
                </a>
              )}
              {last ? null : (
                <IconChevronRight size={12} className={classes.separator} aria-hidden />
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );

  return (
    <header className={classes.header}>
      <div className={classes.text}>
        {titleHidden && help ? (
          <div className={classes.crumbRow}>
            {breadcrumb}
            {help}
          </div>
        ) : (
          breadcrumb
        )}
        <div className={classes.titleRow} data-hidden={titleHidden || undefined}>
          {icon ? (
            <span className={classes.titleIcon} aria-hidden>
              {icon}
            </span>
          ) : null}
          {/* Yol değişince kabuk odağı buraya taşır (M6f G1-23) */}
          <h1 className={classes.title} data-hidden={titleHidden || undefined} tabIndex={-1}>
            {title}
          </h1>
          {titleHidden ? null : help}
        </div>
        {description ? <p className={classes.description}>{description}</p> : null}
      </div>
      {aside || actions ? (
        <div className={classes.side}>
          {aside}
          {actions ? <div className={classes.actions}>{actions}</div> : null}
        </div>
      ) : null}
    </header>
  );
}
